/**
 * CPAYMENT-1716: у тикета нет одного времени жизни на все состояния — таймер принадлежит
 * СОСТОЯНИЮ, и его дедлайн всегда приходит с бэкенда:
 *
 *  - реквизит / загрузка чека (`PAYMENT_PENDING`, `WAITING_FOR_SMS`) — `expiresAt` тикета,
 *    ровно тот момент, по которому его гасит крон;
 *  - авто-проверка чека (`FILES_AUTO_VERIFICATION`) — `receiptVerification.expiresAt`:
 *    бюджет ожидания вердикта либо окно перезаливки, смотря в чём клиент ждёт.
 *
 * Локально дедлайн не выдумываем: страница дважды врала клиенту именно на самодельной формуле.
 */

/** Статусы, на которых работает таймер самого тикета. */
const TICKET_TIMER_STATUSES = [
  'WAITING_FOR_SMS',
  'PAYMENT_PENDING',
  'WAITING_FOR_VARIANT_SELECTION',
];

/**
 * Запас после нуля таймера гейта: тикет двигает демон (проход раз в 3 с), а страница узнаёт об
 * этом полингом (тоже 3 с). Без запаса клиент увидел бы экран поддержки на штатной задержке.
 */
export const GATE_STALL_GRACE_MS = 10_000;

/**
 * Zero on an offer's timer is not yet the end: matching passes over expired offers every 15 s and
 * re-offers the same amounts once, and the page learns of it by polling every 3 s. Only an offer overdue
 * by more than that and a slow response means nothing is coming.
 */
export const OFFER_RENEWAL_GRACE_MS = 30_000;

const parse = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** The offer's time ran out. */
export const hasOfferRunOut = (expireAt?: string | null, now: Date = new Date()): boolean => {
  const deadline = parse(expireAt);
  return !!deadline && now.getTime() >= deadline.getTime();
};

/** The offer's time ran out and no renewal came. */
export const isOfferOverdue = (expireAt?: string | null, now: Date = new Date()): boolean => {
  const deadline = parse(expireAt);
  return !!deadline && now.getTime() - deadline.getTime() > OFFER_RENEWAL_GRACE_MS;
};

/** Дедлайн состояния, в котором тикет находится сейчас. `null` — ждать нечего. */
export const getStateDeadline = (transaction: any): Date | null => {
  if (!transaction) return null;

  if (transaction.status === 'FILES_AUTO_VERIFICATION') {
    return parse(transaction.receiptVerification?.expiresAt);
  }

  if (!TICKET_TIMER_STATUSES.includes(transaction.status)) return null;

  const backendDeadline = parse(transaction.expiresAt);
  if (backendDeadline) return backendDeadline;

  // Фолбэк для не-матчинга: бэкенд дедлайн не считает, ориентир прежний — выдача реквизита
  // (на экране вариантов реквизита ещё нет, там тот же момент приходит как `date`).
  const anchor = parse(transaction.requisite?.date) ?? parse(transaction.date);
  return anchor ? new Date(anchor.getTime() + 10 * 60_000) : null;
};

/**
 * Экран, который вытесняет обычный рендер, когда ожидание кончилось ничем.
 *
 *  - `payment-expired` — клиент дошёл до чека (`receiptIntent`), но тикет истёк: показываем
 *    «время истекло» и путь в поддержку вместо экрана отмены или залипшей формы загрузки.
 *    Ловим оба момента: и `EXPIRED` от крона, и уже истёкший дедлайн, пока крон не добежал
 *    (у матчинга это до 3 минут) — иначе перезагрузка в этом окне уводила клиента к мерчанту.
 *  - `gate-stalled` — чек загружен, но гейт авто-проверки просрочен и тикет так и не сдвинулся
 *    (демон лёг): спиннер крутиться вечно не должен.
 *
 * `DECLINED` сюда намеренно не входит: это отмена, а не истечение.
 */
export type TimeoutScreen = 'payment-expired' | 'gate-stalled' | null;

export const resolveTimeoutScreen = (transaction: any, now: Date = new Date()): TimeoutScreen => {
  if (!transaction) return null;

  if (transaction.status === 'FILES_AUTO_VERIFICATION') {
    const deadline = getStateDeadline(transaction);
    return deadline && now.getTime() >= deadline.getTime() + GATE_STALL_GRACE_MS
      ? 'gate-stalled'
      : null;
  }

  if (!transaction.receiptIntent) return null;

  if (transaction.status === 'EXPIRED') return 'payment-expired';

  if (transaction.status === 'PAYMENT_PENDING' || transaction.status === 'WAITING_FOR_SMS') {
    const deadline = getStateDeadline(transaction);
    return deadline && now.getTime() >= deadline.getTime() ? 'payment-expired' : null;
  }

  return null;
};
